import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  User, Unit, Event, Group, LoginCredentials, ApiResponse, LoginResponse,
  Company, CreateCompanyPayload, UpdateCompanyPayload, CreateCompanyUserPayload,
  CompanyLock, CompanyGroup, LockAssignment, GroupAssignment, AssignLockPayload,
  CompanyUsersPage, CompanyUsersQuery, CompanyUsersImportResult,
} from '../types';


// API base URL configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.lockwaste.com/v1';
const API_KEY = process.env.REACT_APP_API_KEY;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
  },
});

// Interceptor to handle token and errors
api.interceptors.request.use(
  (config) => {
    // Ensure x-api-key is always present
    config.headers['x-api-key'] = API_KEY;
    
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user: User = JSON.parse(userStr);
      if (user.token) {
        config.headers['Authorization'] = `Bearer ${user.token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = api;
  }

  // Authentication
  async login(credentials: LoginCredentials): Promise<ApiResponse<User>> {
    try {
      const response = await this.api.post<LoginResponse>('/users/login', credentials);

      if (response.data.success && response.data.web_user_token) {
        const user: User = {
          id: response.data.user_id,
          name: response.data.name,
          email: response.data.email,
          token: response.data.web_user_token,
        };
        localStorage.setItem('token', user.token as string);
        localStorage.setItem('user', JSON.stringify(user));
        return { success: true, data: user };
      } else {
        return { success: false, data: null, message: 'Login failed' };
      }
    } catch (error: any) {
      console.error('Login failed:', error.message || 'Unknown error');

      if (error.response?.data?.detail) {
        return { success: false, data: null, message: error.response.data.detail };
      }
      return { success: false, data: null, message: 'Connection error. Please check your internet connection.' };
    }
  }

  async logout(): Promise<ApiResponse<null>> {
    try {
      await this.api.post('/auth/logout');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return { success: true, data: null };
    } catch (error) {
      console.error('Logout failed:', error);
      return { success: false, data: null, message: 'Logout failed' };
    }
  }

  // User
  async getCurrentUser(): Promise<ApiResponse<User>> {
    const savedUserStr = localStorage.getItem('user');
    if (!savedUserStr) {
      return { success: false, data: null, message: 'User not found' };
    }
    const user: User = JSON.parse(savedUserStr);
    return { success: true, data: user };
  }

  // Units
  async getUnits(): Promise<ApiResponse<Unit[]>> {
    try {
      const response = await this.api.get<{ owner_units: Unit[]; invite_units: Unit[] }>('/users/units');
      const allUnits = [...response.data.owner_units, ...response.data.invite_units];
      return { success: true, data: allUnits };
    } catch (error) {
      console.error('Error fetching units:', error);
      return { success: false, data: [], message: 'Failed to fetch units' };
    }
  }

  async deleteUnit(id: string, sharedUnit?: boolean): Promise<ApiResponse<void>> {
    try {
      const response: AxiosResponse<ApiResponse<void>> = await this.api.post('/users/delete/units', {
        unit_id: id
      });
      return response.data;
    } catch (error) {
      console.error(`Error deleting unit ${id}:`, error);
      return { success: false, data: null, message: `Failed to delete unit ${id}` };
    }
  }


  // Events
  async getEvents(unitId?: string, startDate?: string, endDate?: string): Promise<ApiResponse<Event[]>> {
    try {
      const params: any = {};
      if (unitId) params.unit_id = unitId;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await this.api.get<Event[]>('/users/units/events', { params });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching events:', error);
      return { success: false, data: [], message: 'Failed to fetch events' };
    }
  }

  async deleteEvents(unitId?: string, startDate?: string, endDate?: string): Promise<ApiResponse<void>> {
    try {
      const body: any = {};
      if (unitId) body.unit_id = unitId;
      if (startDate) body.start_date = startDate;
      if (endDate) body.end_date = endDate;

      const response: AxiosResponse<ApiResponse<void>> = await this.api.post('/users/delete/events', body);
      return response.data;
    } catch (error) {
      console.error('Error deleting events:', error);
      return { success: false, data: null, message: 'Failed to delete events' };
    }
  }

  // Groups
  async getGroups(): Promise<ApiResponse<Group[]>> {
    try {
      const response = await this.api.get<Group[]>('/users/groups');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching groups:', error);
      return { success: false, data: [], message: 'Failed to fetch groups' };
    }
  }

  async deleteGroup(id: string): Promise<ApiResponse<void>> {
    try {
      const response: AxiosResponse<ApiResponse<void>> = await this.api.post('/users/delete/groups', {
        group_id: id
      });
      return response.data;
    } catch (error) {
      console.error(`Error deleting group ${id}:`, error);
      return { success: false, data: null, message: `Failed to delete group ${id}` };
    }
  }

  // Group Management
  async createGroup(name: string): Promise<ApiResponse<{ group_id: string; name: string }>> {
    try {
      const response = await this.api.post('/users/groups', { name });
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error creating group:', error);
      return { success: false, data: null, message: error.response?.data?.message || 'Failed to create group' };
    }
  }

  async addUnitToGroup(unitId: string, groupId: string): Promise<ApiResponse<void>> {
    try {
      await this.api.post('/users/groups/add-unit', { unit_id: unitId, group_id: groupId });
      return { success: true, data: null };
    } catch (error: any) {
      console.error('Error adding unit to group:', error);
      return { success: false, data: null, message: error.response?.data?.message || 'Failed to add unit to group' };
    }
  }

  async removeUnitFromGroup(unitId: string): Promise<ApiResponse<void>> {
    try {
      await this.api.post('/users/groups/remove-unit', { unit_id: unitId });
      return { success: true, data: null };
    } catch (error: any) {
      console.error('Error removing unit from group:', error);
      return { success: false, data: null, message: error.response?.data?.message || 'Failed to remove unit from group' };
    }
  }

  // Password Recovery
  async generateRecoveryCode(email: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post('/recovery/generate', { email });
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Generate recovery code failed:', error);
      if (error.response?.data?.detail) {
        return { success: false, data: null, message: error.response.data.detail };
      }
      return { success: false, data: null, message: 'Failed to generate recovery code' };
    }
  }

  async verifyRecoveryCode(email: string, code: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post('/recovery/verify-confirm', { email, code });
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Verify recovery code failed:', error);
      if (error.response?.data?.detail) {
        return { success: false, data: null, message: error.response.data.detail };
      }
      return { success: false, data: null, message: 'Invalid recovery code' };
    }
  }

  async confirmPasswordReset(email: string, code: string, newPassword: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post('/recovery/confirm', { 
        email, 
        code, 
        password: newPassword 
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Confirm password reset failed:', error);
      if (error.response?.data?.detail) {
        return { success: false, data: null, message: error.response.data.detail };
      }
      return { success: false, data: null, message: 'Failed to reset password' };
    }
  }

  // User Registration
  async generateRegistrationCode(email: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post('/users/register/new', { email });
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Generate registration code failed:', error);
      if (error.response?.data?.detail) {
        return { success: false, data: null, message: error.response.data.detail };
      }
      return { success: false, data: null, message: 'Failed to generate registration code' };
    }
  }

  async verifyRegistrationCode(email: string, code: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post('/users/register/verify', { email, code });
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Verify registration code failed:', error);
      if (error.response?.data?.detail) {
        return { success: false, data: null, message: error.response.data.detail };
      }
      return { success: false, data: null, message: 'Invalid registration code' };
    }
  }

  async completeRegistration(email: string, code: string, password: string, firstName: string, lastName?: string): Promise<ApiResponse<any>> {
    try {
      const registrationData: any = {
        email,
        code,
        password,
        first_name: firstName
      };
      
      if (lastName) {
        registrationData.last_name = lastName;
      }

      // Enviamos todos los datos al endpoint create para completar el registro
      const response = await this.api.post('/users/register/create', registrationData);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Complete registration failed:', error);
      if (error.response?.data?.detail) {
        return { success: false, data: null, message: error.response.data.detail };
      }
      return { success: false, data: null, message: 'Failed to complete registration' };
    }
  }

  // ============ Company (Multi-Enterprise) ============

  async getMyCompanies(): Promise<ApiResponse<Company[]>> {
    try {
      const response = await this.api.get<{ companies: Company[] }>('/company/mine');
      return { success: true, data: response.data.companies };
    } catch (error: any) {
      console.error('Error fetching companies:', error);
      return { success: false, data: [], message: error.response?.data?.detail || 'Failed to fetch companies' };
    }
  }

  async createCompany(payload: CreateCompanyPayload): Promise<ApiResponse<Company>> {
    try {
      const response = await this.api.post('/company/create', payload);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error creating company:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to create company' };
    }
  }

  async updateCompany(companyId: string, payload: UpdateCompanyPayload): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.patch(`/company/${companyId}`, payload);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error updating company:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to update company' };
    }
  }

  async deleteCompany(companyId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.delete(`/company/${companyId}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error deleting company:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to delete company' };
    }
  }

  async getCompanyUsers(companyId: string, query?: CompanyUsersQuery): Promise<ApiResponse<CompanyUsersPage>> {
    try {
      const params: any = {};
      if (query?.search) params.search = query.search;
      if (query?.limit) params.limit = query.limit;
      if (query?.offset != null) params.offset = query.offset;
      const response = await this.api.get<CompanyUsersPage>(`/company/${companyId}/users`, { params });
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error fetching company users:', error);
      return {
        success: false,
        data: { users: [], count: 0, total: 0, limit: query?.limit || 50, offset: query?.offset || 0, has_more: false },
        message: error.response?.data?.detail || 'Failed to fetch users'
      };
    }
  }

  async createCompanyUser(companyId: string, payload: CreateCompanyUserPayload): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post(`/company/${companyId}/users`, payload);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error creating company user:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to create user' };
    }
  }

  async deleteCompanyUser(companyId: string, userId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.delete(`/company/${companyId}/users/${userId}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error deleting company user:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to delete user' };
    }
  }

  async resetCompanyUserPassword(companyId: string, userId: string, newPassword: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.put(`/company/${companyId}/users/${userId}/password`, { new_password: newPassword });
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error resetting user password:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to reset password' };
    }
  }

  async importCompanyUsers(
    companyId: string,
    file: File,
    options?: { dryRun?: boolean; updateExisting?: boolean }
  ): Promise<ApiResponse<CompanyUsersImportResult>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dry_run', String(options?.dryRun || false));
      formData.append('update_existing', String(options?.updateExisting || false));
      const response = await this.api.post<CompanyUsersImportResult>(`/company/${companyId}/users/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error importing company users:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to import users' };
    }
  }

  // ============ Company Lock Assignments ============

  async getCompanyLocks(companyId: string): Promise<ApiResponse<CompanyLock[]>> {
    try {
      const response = await this.api.get<{ locks: CompanyLock[]; count: number }>(`/company/${companyId}/locks`);
      return { success: true, data: response.data.locks };
    } catch (error: any) {
      console.error('Error fetching company locks:', error);
      return { success: false, data: [], message: error.response?.data?.detail || 'Failed to fetch locks' };
    }
  }

  async getCompanyGroups(companyId: string): Promise<ApiResponse<CompanyGroup[]>> {
    try {
      const response = await this.api.get<{ groups: CompanyGroup[]; count: number }>(`/company/${companyId}/groups`);
      return { success: true, data: response.data.groups };
    } catch (error: any) {
      console.error('Error fetching company groups:', error);
      return { success: false, data: [], message: error.response?.data?.detail || 'Failed to fetch groups' };
    }
  }

  async getUserLockAssignments(companyId: string, userId: string): Promise<ApiResponse<{ shared_units: LockAssignment[]; shared_groups: GroupAssignment[] }>> {
    try {
      const response = await this.api.get<{ shared_units: LockAssignment[]; shared_groups: GroupAssignment[] }>(`/company/${companyId}/users/${userId}/locks`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error fetching user lock assignments:', error);
      return { success: false, data: { shared_units: [], shared_groups: [] }, message: error.response?.data?.detail || 'Failed to fetch assignments' };
    }
  }

  async assignLockToUser(companyId: string, userId: string, payload: AssignLockPayload): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post(`/company/${companyId}/users/${userId}/locks`, payload);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error assigning lock:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to assign lock' };
    }
  }

  async unassignLockFromUser(companyId: string, userId: string, assignmentId: string, type: 'unit' | 'group' = 'unit'): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.delete(`/company/${companyId}/users/${userId}/locks/${assignmentId}?type=${type}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error unassigning lock:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to unassign lock' };
    }
  }

  async updateLockAssignment(companyId: string, userId: string, assignmentId: string, allowSettings: boolean, type: 'unit' | 'group' = 'unit'): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.patch(`/company/${companyId}/users/${userId}/locks/${assignmentId}?type=${type}`, { allow_settings_changes: allowSettings });
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error updating assignment:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to update assignment' };
    }
  }

  // ─── Sharing / Members ──────────────────────────────────────

  async getGroupMembers(groupId: string): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.api.get(`/users/groups/${groupId}/members`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error fetching group members:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to fetch group members' };
    }
  }

  async removeGroupMember(groupId: string, sharedGroupId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.delete(`/users/groups/${groupId}/members/${sharedGroupId}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error removing group member:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to remove group member' };
    }
  }

  async getUnitMembers(unitId: string): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.api.get(`/users/units/${unitId}/members`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error fetching unit members:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to fetch unit members' };
    }
  }

  async removeUnitMember(unitId: string, sharedUnitId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.delete(`/users/units/${unitId}/members/${sharedUnitId}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error removing unit member:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to remove unit member' };
    }
  }

  // ─── User Profile ──────────────────────────────────────────

  async updateUserProfile(data: { first_name?: string; last_name?: string }): Promise<ApiResponse<{ name: string; first_name: string; last_name: string }>> {
    try {
      const response = await this.api.patch('/users/profile', data);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('Error updating user profile:', error);
      return { success: false, data: null, message: error.response?.data?.detail || 'Failed to update profile' };
    }
  }
}

export const apiService = new ApiService();
